/**
 * Decomposes the store editorial blob (couponPageDescription) into 4 named
 * sections: FAQ, Contato, Sobre, Outras informações. Each maps to a different
 * SEO surface (FAQPage JSON-LD, Store.contactPoint, Store.description, raw
 * HTML render). Stores whose content is not yet in the section format degrade
 * gracefully — the whole blob falls into `outras` and renders as today.
 *
 * Convention documented in
 * promobit-ai/kb/conventions/store-page-content.md (referenced by the admin
 * tooltip in malote).
 */

const SECTION_ALIASES = {
  faq: ['faq', 'perguntas frequentes', 'duvidas frequentes'],
  contato: ['contato', 'fale conosco', 'atendimento'],
  sobre: ['sobre', 'sobre a loja', 'quem somos'],
  outras: ['outras informacoes', 'mais informacoes'],
};

const SOCIAL_HOST_RE =
  /(?:facebook\.com|twitter\.com|x\.com|instagram\.com|youtube\.com|tiktok\.com)/i;
const CUSTOMER_SERVICE_RE =
  /(ajuda|atendimento|help|customer|suporte|support)/i;
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/;
const HEADING_CONNECTORS_RE = /\b(a|de|da|do|para|the)\b/g;

const normalize = (text) =>
  (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const matchSectionKey = (headingText) => {
  const normalized = normalize(headingText);

  // Match if the heading equals an alias OR starts with an alias followed by
  // a token boundary (space). Lets "Sobre a Amazon", "FAQ Amazon" or
  // "Perguntas frequentes — Amazon" map cleanly without forcing the editor to
  // strip the brand name from the heading.
  for (const [key, aliases] of Object.entries(SECTION_ALIASES)) {
    const match = aliases.some(
      (alias) =>
        normalized === alias ||
        normalized.startsWith(`${alias} `) ||
        normalized.startsWith(`${alias}-`) ||
        normalized.startsWith(`${alias}—`) ||
        normalized.startsWith(`${alias}:`)
    );
    if (match) return key;
  }

  return null;
};

/**
 * Splits HTML by `<h2>` boundaries and groups content under the four section
 * keys. Anything appearing before the first matched `<h2>` or under unmatched
 * `<h2>`s falls into `outras`.
 *
 * `hasRecognizedSection` distinguishes "user followed the convention with at
 * least one recognized heading" from "legacy/free-form content". Needed so
 * the FAQ parser only falls back to the full blob when truly legacy — when
 * the user wrote `<h2>Outras informações</h2>` (recognized) but no FAQ, we
 * must NOT scan the whole blob (would surface `<h3>?</h3>` from Outras as
 * false FAQ items).
 *
 * @param {string} html
 * @returns {{
 *   faq: string|null,
 *   contato: string|null,
 *   sobre: string|null,
 *   outras: string|null,
 *   hasRecognizedSection: boolean
 * }}
 */
export const parseStoreContentSections = (html) => {
  const empty = {
    faq: null,
    contato: null,
    sobre: null,
    outras: null,
    hasRecognizedSection: false,
  };

  if (!html || typeof html !== 'string') return empty;

  try {
    const headerRegex = /<h2[^>]*>([\s\S]*?)<\/h2>/gi;
    const matches = [];
    let m;

    while ((m = headerRegex.exec(html)) !== null) {
      matches.push({
        headingText: m[1],
        start: m.index,
        end: m.index + m[0].length,
      });
    }

    const buckets = { faq: '', contato: '', sobre: '', outras: '' };
    let hasRecognizedSection = false;

    if (matches.length === 0) {
      const trimmed = html.trim();
      return trimmed
        ? {
            faq: null,
            contato: null,
            sobre: null,
            outras: trimmed,
            hasRecognizedSection: false,
          }
        : empty;
    }

    const preamble = html.slice(0, matches[0].start).trim();
    if (preamble) {
      buckets.outras += preamble;
    }

    for (let i = 0; i < matches.length; i += 1) {
      const current = matches[i];
      const next = matches[i + 1];
      const sectionEnd = next ? next.start : html.length;
      const sectionKey = matchSectionKey(current.headingText);

      const block = html.slice(current.start, sectionEnd).trim();
      if (!block) continue;

      if (sectionKey) {
        hasRecognizedSection = true;
        const body = html.slice(current.end, sectionEnd).trim();
        buckets[sectionKey] += body;
      } else {
        buckets.outras += (buckets.outras ? '\n' : '') + block;
      }
    }

    return {
      faq: buckets.faq || null,
      contato: buckets.contato || null,
      sobre: buckets.sobre || null,
      outras: buckets.outras || null,
      hasRecognizedSection,
    };
  } catch {
    return {
      faq: null,
      contato: null,
      sobre: null,
      outras: html,
      hasRecognizedSection: false,
    };
  }
};

/**
 * Removes a leading `<h2>` or `<h3>` from a section's HTML when its text is a
 * variation of the section's label — the template renders the section heading
 * already, so the inner heading would duplicate it (e.g. "Sobre a Amazon"
 * inside the Sobre section).
 *
 * @param {string} html
 * @param {string} sectionLabel - e.g. "Sobre", "FAQ", "Contato"
 * @returns {string}
 */
export const stripDuplicateLeadingHeading = (html, sectionLabel) => {
  if (!html || typeof html !== 'string' || !sectionLabel) return html;

  const labelNormalized = normalize(sectionLabel).replace(
    HEADING_CONNECTORS_RE,
    ' '
  );
  const labelTokens = labelNormalized.split(/\s+/).filter((t) => t.length > 1);

  if (labelTokens.length === 0) return html;

  return html.replace(
    /^\s*<(h[23])[^>]*>([\s\S]*?)<\/\1>\s*/i,
    (match, _tag, headingText) => {
      const headingNormalized = normalize(headingText).replace(
        HEADING_CONNECTORS_RE,
        ' '
      );

      // Strip if all label tokens are present in the heading (e.g. "Sobre Amazon")
      // OR if only the first label token matches (e.g. <h2>Sobre</h2> for label "Sobre Amazon").
      // The looser branch covers the case where the admin omitted the store name.
      const allTokensPresent = labelTokens.every((token) =>
        headingNormalized.includes(token)
      );

      const headingTokens = headingNormalized
        .split(/\s+/)
        .filter((t) => t.length > 0);
      const onlyFirstTokenMatches =
        headingTokens.length > 0 &&
        headingTokens.every((t) => t === labelTokens[0]);

      return allTokensPresent || onlyFirstTokenMatches ? '' : match;
    }
  );
};

/**
 * Extracts contact signals from the Contato section HTML for use in
 * Store.contactPoint and Organization.sameAs schemas.
 *
 * `customerServiceUrl` is restricted to URLs whose host matches the store's
 * own domain (or a subdomain of it) — prevents an editor from injecting a
 * third-party "atendimento.evil.example/help" URL that would surface in the
 * Store JSON-LD as the official support link.
 *
 * @param {string} html
 * @param {{ storeDomain?: string|null }} [opts]
 * @returns {{ email: string|null, customerServiceUrl: string|null, sameAs: string[] }}
 */
export const extractContactSignals = (html, { storeDomain } = {}) => {
  const empty = { email: null, customerServiceUrl: null, sameAs: [] };

  if (!html || typeof html !== 'string') return empty;

  try {
    const text = htmlToPlainText(html);
    const emailMatch = text.match(EMAIL_RE);

    const hrefRegex = /href=["'](https?:\/\/[^"']+)["']/gi;
    const urls = [];
    let m;
    while ((m = hrefRegex.exec(html)) !== null) {
      urls.push(m[1]);
    }

    const sameAsSet = new Set(urls.filter((u) => SOCIAL_HOST_RE.test(u)));

    const normalizedStoreDomain = (storeDomain || '')
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .toLowerCase();

    const matchesStoreDomain = (url) => {
      if (!normalizedStoreDomain) return false;
      try {
        const host = new URL(url).hostname.toLowerCase().replace(/^www\./, '');
        return (
          host === normalizedStoreDomain ||
          host.endsWith(`.${normalizedStoreDomain}`)
        );
      } catch {
        return false;
      }
    };

    const customerServiceUrl =
      urls.find(
        (u) =>
          !SOCIAL_HOST_RE.test(u) &&
          CUSTOMER_SERVICE_RE.test(u) &&
          matchesStoreDomain(u)
      ) ?? null;

    return {
      email: emailMatch ? emailMatch[0] : null,
      customerServiceUrl,
      sameAs: Array.from(sameAsSet),
    };
  } catch {
    return empty;
  }
};

/**
 * Strips HTML tags and collapses whitespace.
 *
 * @param {string} html
 * @returns {string}
 */
export const htmlToPlainText = (html) => {
  if (!html || typeof html !== 'string') return '';

  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
};
